import AddIcon from "@mui/icons-material/Add";
import ArchiveOutlinedIcon from "@mui/icons-material/ArchiveOutlined";
import AttachFileOutlinedIcon from "@mui/icons-material/AttachFileOutlined";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, InputAdornment, MenuItem, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, Tooltip, Typography,
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";

import { OUTIL_COLORS, paperSx, tableHeaderSx } from "./OutilContainer.styles";

const EMPTY_DOCUMENT = {
  approvalDate: "", approver: "", archiveLocation: "", code: "",
  documentType: "procedure", editor: "", process: "", revisionDate: "",
  status: "draft", title: "", verifier: "", version: "1.0",
};
const EMPTY_PROCEDURE = {
  approval: "", archiving: "", codification: "", object: "", revision: "",
};
const SEARCH_FIELDS = [
  "code", "title", "documentType", "version", "process", "editor",
  "verifier", "approver", "approvalDate", "revisionDate", "status",
];
const DOCUMENT_TYPES = [
  ["procedure", "Procédure"], ["instruction", "Instruction"],
  ["form", "Formulaire"], ["record", "Enregistrement"],
  ["policy", "Politique"], ["manual", "Manuel"], ["other", "Autre"],
];
const STATUSES = [
  ["draft", "Brouillon"], ["active", "En vigueur"],
  ["archived", "Archivé"], ["obsolete", "Obsolète"],
];
const COLUMNS = [
  ["Code", 115], ["Titre", 190], ["Type", 125], ["Ver.", 80],
  ["Processus", 135], ["Rédacteur", 135], ["Approbateur", 135],
  ["Date appro.", 115], ["Date révision", 115], ["Statut", 110],
  ["PJ", 70], ["Actions", 100],
];
const PROCEDURE_FIELDS = [
  ["object", "Objet"], ["codification", "Codification"],
  ["approval", "Approbation"], ["archiving", "Archivage"],
  ["revision", "Révision"],
];
const normalize = (value) => String(value || "").normalize("NFD")
  .replace(/[\u0300-\u036f]/g, "").toLocaleLowerCase("fr");
const createId = () => `document-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
const getLabel = (options, key) =>
  options.find(([value]) => value === key)?.[1] || key || "—";

const textareaSx = {
  backgroundColor: "#fff", border: `1px solid ${OUTIL_COLORS.border}`,
  borderRadius: "6px", boxSizing: "border-box", color: OUTIL_COLORS.text,
  fontFamily: "'DM Sans', sans-serif", fontSize: 11.5, lineHeight: 1.45,
  minHeight: 68, outline: "none", p: 1, resize: "vertical", width: "100%",
  "&:disabled": { backgroundColor: "#f3f6f9", color: OUTIL_COLORS.textMuted },
  "&:focus": { borderColor: OUTIL_COLORS.navy, boxShadow: "0 0 0 2px rgba(23,61,97,.12)" },
};
const goldButtonSx = {
  backgroundColor: OUTIL_COLORS.gold, color: OUTIL_COLORS.text,
  fontSize: 10, fontWeight: 700, textTransform: "none",
  "&:hover": { backgroundColor: "#c79c2f" },
};
const sectionSx = {
  backgroundColor: "#f8fafc", border: `1px solid ${OUTIL_COLORS.border}`,
  borderRadius: "8px", p: 1.4,
};

const FieldLabel = ({ children, htmlFor }) => (
  <Typography
    color="#526b84" component="label" display="block" fontSize={9.5}
    fontWeight={700} htmlFor={htmlFor} letterSpacing=".04em" mb={0.45}
    textTransform="uppercase"
  >
    {children}
  </Typography>
);
const InputField = ({ field, label, onChange, type = "text", value }) => (
  <Box>
    <FieldLabel htmlFor={`document-${field}`}>{label}</FieldLabel>
    <TextField
      fullWidth id={`document-${field}`}
      onChange={(event) => onChange(field, event.target.value)}
      size="small" type={type} value={value || ""}
    />
  </Box>
);
const FormSection = ({ children, icon, title }) => (
  <Paper elevation={0} sx={sectionSx}>
    <Typography
      color={OUTIL_COLORS.navy} fontSize={10.5} fontWeight={800}
      sx={{ borderBottom: `1px solid ${OUTIL_COLORS.border}`, mb: 1.2, pb: 0.75 }}
      textTransform="uppercase"
    >
      {icon}{title}
    </Typography>
    {children}
  </Paper>
);

const DocumentDialog = ({ form, onChange, onClose, onDelete, onSave, open }) => (
  <Dialog
    fullWidth maxWidth="md" onClose={onClose} open={open}
    PaperProps={{ sx: { borderRadius: "14px", maxHeight: "92vh" } }}
  >
    <DialogTitle sx={{ alignItems: "center", borderBottom: `1px solid ${OUTIL_COLORS.border}`, display: "flex", fontFamily: "Georgia, serif", fontSize: 17, fontWeight: 700, justifyContent: "space-between", px: 2.2, py: 1.4 }}>
      <Box component="span">
        <FolderOutlinedIcon sx={{ color: "#d7a328", fontSize: 19, mr: 0.8, verticalAlign: "middle" }} />
        Document — Fiche information documentée
      </Box>
      <Button color="error" onClick={onClose} size="small" startIcon={<CloseIcon />} sx={{ textTransform: "none" }} variant="outlined">Fermer</Button>
    </DialogTitle>
    <DialogContent sx={{ p: "18px !important" }}>
      <Box sx={{ display: "grid", gap: 1.2 }}>
        <Paper elevation={0} sx={{ alignItems: "center", backgroundColor: "#edf3f8", border: `1px solid ${OUTIL_COLORS.border}`, borderRadius: "7px", display: "flex", justifyContent: "space-between", px: 1.4, py: 1 }}>
          <Typography color={OUTIL_COLORS.textMuted} fontSize={10.5}>
            {form.id ? "Modification de l'entrée sélectionnée" : "Nouvelle information documentée"}
          </Typography>
          {form.id && <Button color="error" onClick={onDelete} size="small" startIcon={<DeleteOutlineIcon />} sx={{ fontSize: 10, textTransform: "none" }} variant="contained">Supprimer cette entrée</Button>}
        </Paper>

        <FormSection icon={<AttachFileOutlinedIcon sx={{ fontSize: 14, mr: 0.6, verticalAlign: "middle" }} />} title="Pièces jointes (0)">
          <Typography color={OUTIL_COLORS.textMuted} fontSize={10.5}>Aucune pièce jointe</Typography>
          <Tooltip title="Ajout de pièce jointe disponible ultérieurement">
            <span><Button disabled size="small" startIcon={<AttachFileOutlinedIcon />} sx={{ fontSize: 10.5, mt: 0.5, textTransform: "none" }}>Ajouter une pièce jointe</Button></span>
          </Tooltip>
        </FormSection>

        <FormSection icon={<EditOutlinedIcon sx={{ color: "#d49b17", fontSize: 14, mr: 0.6, verticalAlign: "middle" }} />} title="Identification du document">
          <Box sx={{ display: "grid", gap: 1.1, gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" } }}>
            <InputField field="code" label="Code / référence" onChange={onChange} value={form.code} />
            <InputField field="version" label="Version" onChange={onChange} value={form.version} />
            <Box sx={{ gridColumn: { sm: "1 / -1" } }}><InputField field="title" label="Titre complet du document" onChange={onChange} value={form.title} /></Box>
            <Box>
              <FieldLabel htmlFor="document-type">Type de document</FieldLabel>
              <TextField fullWidth id="document-type" onChange={(event) => onChange("documentType", event.target.value)} select size="small" value={form.documentType || "procedure"}>
                {DOCUMENT_TYPES.map(([key, label]) => <MenuItem key={key} value={key}>{label}</MenuItem>)}
              </TextField>
            </Box>
            <InputField field="process" label="Processus associé" onChange={onChange} value={form.process} />
          </Box>
        </FormSection>

        <FormSection icon={<DescriptionOutlinedIcon sx={{ color: "#d49b17", fontSize: 14, mr: 0.6, verticalAlign: "middle" }} />} title="Circuit d'approbation">
          <Box sx={{ display: "grid", gap: 1.1, gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" } }}>
            <InputField field="editor" label="Rédacteur" onChange={onChange} value={form.editor} />
            <InputField field="verifier" label="Vérificateur" onChange={onChange} value={form.verifier} />
            <InputField field="approver" label="Approbateur" onChange={onChange} value={form.approver} />
            <InputField field="approvalDate" label="Date d'approbation" onChange={onChange} type="date" value={form.approvalDate} />
            <InputField field="revisionDate" label="Date de révision prévue" onChange={onChange} type="date" value={form.revisionDate} />
          </Box>
        </FormSection>

        <FormSection icon={<ArchiveOutlinedIcon sx={{ color: "#986c45", fontSize: 14, mr: 0.6, verticalAlign: "middle" }} />} title="Gestion et archivage">
          <Box sx={{ display: "grid", gap: 1.1, gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" } }}>
            <InputField field="archiveLocation" label="Lieu / mode d'archivage" onChange={onChange} value={form.archiveLocation} />
            <Box>
              <FieldLabel htmlFor="document-status">Statut actuel</FieldLabel>
              <TextField fullWidth id="document-status" onChange={(event) => onChange("status", event.target.value)} select size="small" value={form.status || "draft"}>
                {STATUSES.map(([key, label]) => <MenuItem key={key} value={key}>{label}</MenuItem>)}
              </TextField>
            </Box>
          </Box>
        </FormSection>
      </Box>
    </DialogContent>
    <DialogActions sx={{ borderTop: `1px solid ${OUTIL_COLORS.border}`, px: 2.2, py: 1.25 }}>
      <Button onClick={onClose} sx={{ color: OUTIL_COLORS.text, textTransform: "none" }}>Annuler</Button>
      <Button onClick={onSave} startIcon={<SaveOutlinedIcon />} sx={{ backgroundColor: "#27884f", fontWeight: 700, textTransform: "none", "&:hover": { backgroundColor: "#1f7040" } }} variant="contained">Valider et sauvegarder</Button>
    </DialogActions>
  </Dialog>
);

const MasteryProcedure = ({ onChange, value }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({ ...EMPTY_PROCEDURE, ...(value.documentMastery || {}) });

  useEffect(() => {
    setDraft({ ...EMPTY_PROCEDURE, ...(value.documentMastery || {}) });
    setEditing(false);
  }, [value.documentMastery]);

  const save = () => {
    onChange("documentMastery", { ...draft });
    setEditing(false);
  };
  const reset = () => {
    const empty = { ...EMPTY_PROCEDURE };
    setDraft(empty);
    onChange("documentMastery", empty);
    setEditing(true);
  };

  return (
    <Paper aria-label="Procédure de maîtrise documentaire" elevation={0} role="region" sx={{ ...paperSx, overflow: "hidden" }}>
      <Box sx={{ alignItems: { xs: "flex-start", sm: "center" }, backgroundColor: OUTIL_COLORS.panelHeader, borderBottom: `1px solid ${OUTIL_COLORS.border}`, display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 1, justifyContent: "space-between", minHeight: 48, px: 1.75, py: 0.8 }}>
        <Typography color={OUTIL_COLORS.navy} fontSize={12} fontWeight={700}>
          <DescriptionOutlinedIcon sx={{ fontSize: 16, mr: 0.7, verticalAlign: "middle" }} />
          Procédure de maîtrise documentaire
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
          <Button disabled={editing} onClick={() => setEditing(true)} size="small" startIcon={<EditOutlinedIcon />} sx={{ color: OUTIL_COLORS.navy, fontSize: 10, textTransform: "none" }} variant="outlined">Modifier</Button>
          <Button disabled={!editing} onClick={save} size="small" startIcon={<SaveOutlinedIcon />} sx={{ backgroundColor: "#27884f", fontSize: 10, textTransform: "none", "&:hover": { backgroundColor: "#1f7040" } }} variant="contained">Enregistrer</Button>
          <Button onClick={reset} size="small" startIcon={<RestartAltIcon />} sx={{ fontSize: 10, textTransform: "none" }} variant="outlined">Réinitialiser</Button>
        </Box>
      </Box>
      <Box sx={{ display: "grid", gap: 1.35, gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" }, p: 1.75 }}>
        {PROCEDURE_FIELDS.map(([field, label]) => (
          <Box key={field}>
            <FieldLabel htmlFor={`mastery-${field}`}>{label}</FieldLabel>
            <Box
              aria-label={label} component="textarea" disabled={!editing}
              id={`mastery-${field}`}
              onChange={(event) => setDraft((current) => ({ ...current, [field]: event.target.value }))}
              rows={3} sx={textareaSx} value={draft[field] || ""}
            />
          </Box>
        ))}
      </Box>
    </Paper>
  );
};

const InformationsDocumentees = ({ onChange, value = {} }) => {
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ ...EMPTY_DOCUMENT });
  const rows = useMemo(
    () => (Array.isArray(value.documentRows) ? value.documentRows : []),
    [value.documentRows]
  );
  const filteredRows = useMemo(() => {
    const query = normalize(search).trim();
    return query
      ? rows.filter((row) => SEARCH_FIELDS.some((field) => normalize(row[field]).includes(query)))
      : rows;
  }, [rows, search]);

  const setRows = (nextRows) => onChange("documentRows", nextRows);
  const openCreate = () => {
    setForm({ ...EMPTY_DOCUMENT });
    setDialogOpen(true);
  };
  const openEdit = (row) => {
    setForm({ ...EMPTY_DOCUMENT, ...row });
    setDialogOpen(true);
  };
  const saveDocument = () => {
    setRows(form.id
      ? rows.map((row) => (row.id === form.id ? { ...form } : row))
      : [...rows, { ...form, id: createId() }]);
    setDialogOpen(false);
  };
  const deleteDocument = () => {
    if (form.id) setRows(rows.filter((row) => row.id !== form.id));
    setDialogOpen(false);
  };

  return (
    <Box sx={{ display: "grid", gap: 1.5 }}>
      <Paper elevation={0} sx={{ alignItems: "center", backgroundColor: "#e8f0f1", border: `1px solid ${OUTIL_COLORS.border}`, borderLeft: `3px solid ${OUTIL_COLORS.gold}`, borderRadius: "7px", display: "flex", flexWrap: "wrap", gap: 1.2, px: 1.5, py: 1 }}>
        <DescriptionOutlinedIcon sx={{ color: "#8c7aa8", fontSize: 18 }} />
        <Typography color={OUTIL_COLORS.navy} fontSize={10.5} fontWeight={700}>OUTIL_10_Maitrise_documentaire.docx</Typography>
        <Tooltip title="Téléchargement disponible ultérieurement">
          <span><Button disabled size="small" startIcon={<FileDownloadOutlinedIcon />} sx={{ fontSize: 10, textTransform: "none" }} variant="contained">Télécharger le template</Button></span>
        </Tooltip>
      </Paper>

      <Paper aria-label="Informations documentées" elevation={0} role="region" sx={{ ...paperSx, overflow: "hidden" }}>
        <Box sx={{ alignItems: "center", backgroundColor: OUTIL_COLORS.panelHeader, borderBottom: `1px solid ${OUTIL_COLORS.border}`, display: "flex", justifyContent: "space-between", minHeight: 48, px: 1.75 }}>
          <Typography color={OUTIL_COLORS.navy} fontSize={12} fontWeight={700}>
            <FolderOutlinedIcon sx={{ color: "#d7a328", fontSize: 16, mr: 0.7, verticalAlign: "middle" }} />
            Informations documentées — §7.5
          </Typography>
          <Button onClick={openCreate} size="small" startIcon={<AddIcon />} sx={goldButtonSx} variant="contained">Ajouter</Button>
        </Box>
        <Box sx={{ alignItems: "center", backgroundColor: "#f5f8fb", border: `1px solid ${OUTIL_COLORS.border}`, borderRadius: "6px", display: "flex", gap: 0.7, mx: 1.5, my: 1, px: 1.2, py: 0.8 }}>
          <DescriptionOutlinedIcon sx={{ color: OUTIL_COLORS.gold, fontSize: 16 }} />
          <Typography color={OUTIL_COLORS.textMuted} fontSize={10.5}>Cliquez sur une ligne pour ouvrir la fiche détail complète.</Typography>
        </Box>
        <Box sx={{ alignItems: "center", backgroundColor: "#edf3f8", display: "flex", flexWrap: "wrap", gap: 1, px: 1.5, py: 1 }}>
          <Typography color={OUTIL_COLORS.textMuted} fontSize={9.5} fontWeight={700}>RECHERCHE :</Typography>
          <TextField
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 16 }} /></InputAdornment> }}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Filtrer toutes les colonnes..." size="small"
            sx={{ flex: 1, minWidth: 220, "& .MuiInputBase-root": { backgroundColor: "#fff", fontSize: 10.5, height: 34 } }}
            value={search}
          />
          <Typography color={OUTIL_COLORS.textMuted} fontSize={10}>{filteredRows.length} / {rows.length} entrée(s)</Typography>
          <Button disabled={!search} onClick={() => setSearch("")} size="small" startIcon={<RestartAltIcon />} sx={{ color: OUTIL_COLORS.navy, fontSize: 10, fontWeight: 700, textTransform: "none" }}>Réinit.</Button>
        </Box>
        <TableContainer sx={{ overflowX: "auto" }}>
          <Table aria-label="Registre des informations documentées" size="small" sx={{ minWidth: 1450, tableLayout: "fixed" }}>
            <TableHead><TableRow>{COLUMNS.map(([label, width]) => <TableCell key={label} sx={{ ...tableHeaderSx, width }}>{label}</TableCell>)}</TableRow></TableHead>
            <TableBody>
              {!filteredRows.length ? (
                <TableRow><TableCell align="center" colSpan={COLUMNS.length} sx={{ color: "#8091a3", fontSize: 11, py: 2.2 }}>Aucun document enregistré — cliquez sur Ajouter.</TableCell></TableRow>
              ) : filteredRows.map((row) => {
                const values = [
                  row.code, row.title, getLabel(DOCUMENT_TYPES, row.documentType),
                  row.version, row.process, row.editor, row.approver,
                  row.approvalDate, row.revisionDate,
                ];
                return (
                  <TableRow hover key={row.id} onClick={() => openEdit(row)} sx={{ cursor: "pointer", "&:nth-of-type(even)": { backgroundColor: "#f7f9fc" } }}>
                    {values.map((cell, index) => <TableCell key={COLUMNS[index][0]}>{cell || "—"}</TableCell>)}
                    <TableCell>
                      <Chip
                        label={getLabel(STATUSES, row.status)} size="small"
                        sx={{
                          backgroundColor: row.status === "active" ? "#d9f7e5" : "#e7edf4",
                          color: row.status === "active" ? "#147a43" : OUTIL_COLORS.navy,
                          fontSize: 9.5, fontWeight: 700,
                        }}
                      />
                    </TableCell>
                    <TableCell>—</TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", gap: 0.5 }}>
                        <Tooltip title="Entrée sauvegardée localement">
                          <IconButton aria-label="Entrée sauvegardée localement" onClick={(event) => event.stopPropagation()} size="small" sx={{ backgroundColor: "#27884f", borderRadius: "6px", color: "#fff" }}>
                            <SaveOutlinedIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Modifier">
                          <IconButton aria-label="Modifier le document" onClick={(event) => { event.stopPropagation(); openEdit(row); }} size="small" sx={{ backgroundColor: OUTIL_COLORS.navy, borderRadius: "6px", color: "#fff" }}>
                            <EditOutlinedIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <MasteryProcedure onChange={onChange} value={value} />
      <DocumentDialog
        form={form}
        onChange={(field, nextValue) => setForm((current) => ({ ...current, [field]: nextValue }))}
        onClose={() => setDialogOpen(false)}
        onDelete={deleteDocument}
        onSave={saveDocument}
        open={dialogOpen}
      />
    </Box>
  );
};

export default InformationsDocumentees;